import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faRobot, faTrophy, faSkull, faMinus, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { getSportIcon } from '../utils/sportEmoji';
import { Activity, DailyChallenge, Opponent, PlayerStats } from '../types/activity';
import WeeklyCalendar from '../components/WeeklyCalendar';
import { getEloColor } from '../utils/ladder';
import { getDaysFromCycleStart } from '../utils/dateUtils';
import { isToday } from '../utils/dateUtils';
import logo from '../assets/logo.png';

/* ── Sport colors for points bars (must match DuelScorePage) ── */
import { BarSegment, buildSportSegments } from '../utils/sportData';


/* ── Types ── */

interface WeekEntry {
  week_label: string;
  start_at_utc: string;
  match_count: number;
}

interface WeeksResponse {
  weeks: WeekEntry[];
  my_league: number;
  my_player: { id: number; display_name: string; avatar_url: string; elo_rating?: number };
}

interface WeekMatchPlayer {
  id: number;
  display_name: string;
  avatar_url: string;
  is_bot: boolean;
  is_paused?: boolean;
  level_value: number;
  league: number;
  points: number;
  activity_count: number;
}

interface WeekMatch {
  matchup_id: number;
  winner_id: number | null;
  is_my_match: boolean;
  is_bot_match: boolean;
  player_a: WeekMatchPlayer;
  player_b: WeekMatchPlayer;
}

interface WeekMatchesResponse {
  week_label: string;
  cycle: { id: number; week_label: string; start_at_utc: string; workout_deadline_utc: string; };
  matchups: WeekMatch[];
  my_league: number;
}

interface DetailPlayer {
  id: number;
  display_name: string;
  avatar_url: string;
  is_bot: boolean;
  level_value: number;
  activities: Activity[];
  consolation_points: number;
}

interface MatchDetail {
  cycle: { id: number; week_label: string; start_at_utc: string; workout_deadline_utc: string; };
  is_completed: boolean;
  player_a: DetailPlayer;
  player_b: DetailPlayer;
}

interface Props {
  onBack: () => void;
  onReplay: (matchupId: number) => void;  onNavigateToPlayerProfile?: (playerId: number) => void;}

/* ── Component ── */

export default function ArchivePage({ onBack, onReplay, onNavigateToPlayerProfile }: Props) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [weeks, setWeeks] = useState<WeekEntry[]>([]);
  const [myLeague, setMyLeague] = useState(1);
  const [myPlayer, setMyPlayer] = useState<{ id: number; display_name: string; avatar_url: string; elo_rating?: number } | null>(null);
  const [selectedWeek, setSelectedWeek] = useState<string | null>(null);

  // Week matches
  const [weekData, setWeekData] = useState<WeekMatchesResponse | null>(null);
  const [weekLoading, setWeekLoading] = useState(false);

  // Your Results (flache Liste aller eigenen Matches)
  const [yourResultsOpen, setYourResultsOpen] = useState(false);
  const [yourResults, setYourResults] = useState<any[]>([]);
  const [yourResultsLoading, setYourResultsLoading] = useState(false);
  const [yourResultsPage, setYourResultsPage] = useState(1);
  const [yourResultsHasMore, setYourResultsHasMore] = useState(true);

  // Your Results laden
  const loadYourResults = useCallback(async (page: number, append = false) => {
    if (!token) return;
    setYourResultsLoading(true);
    try {
      const data = await api.get(`/duel/archive?page=${page}&limit=20`, token);
      if (data?.matches) {
        setYourResults(prev => append ? [...prev, ...data.matches] : data.matches);
        setYourResultsHasMore(data.matches.length === 20);
      }
    } catch {}
    setYourResultsLoading(false);
  }, [token]);

  // Beim Öffnen laden
  useEffect(() => {
    if (yourResultsOpen && yourResults.length === 0) {
      loadYourResults(1);
    }
  }, [yourResultsOpen, loadYourResults, yourResults.length]);

  // Calendar expansion
  const [expandedMatchId, setExpandedMatchId] = useState<number | null>(null);
  const detailCache = useRef(new Map<number, MatchDetail | 'loading'>());
  const statsCache = useRef(new Map<number, PlayerStats>());
  const detailStatsCache = useRef(new Map<number, { myStats: PlayerStats | null; oppStats: PlayerStats | null }>());
  const [weekChallenges, setWeekChallenges] = useState<Record<string, DailyChallenge>>({});
  const [mobileDayIdx, setMobileDayIdx] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [renderTick, setRenderTick] = useState(0);
  const [barTip, setBarTip] = useState<{ x: number; y: number; seg: BarSegment } | null>(null);
  const isTouch = useRef(typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches);

  // Close bar tooltip on scroll or resize
  useEffect(() => {
    if (!barTip) return;
    const close = () => setBarTip(null);
    window.addEventListener('scroll', close, { once: true, capture: true });
    window.addEventListener('resize', close, { once: true });
    return () => {
      window.removeEventListener('scroll', close, { capture: true } as any);
      window.removeEventListener('resize', close);
    };
  }, [barTip]);

  // On mobile: track which day is centered for cumulative bar filter
  const handleMobileDayChange = useCallback((dayIndex: number) => {
    setMobileDayIdx(dayIndex);
    setBarTip(null);
  }, []);

  // Load weeks list
  useEffect(() => {
    if (!token) return;
    api.get('/duel/archive/weeks', token).then((data: WeeksResponse) => {
      if (data?.weeks) {
        setWeeks(data.weeks);
        setMyLeague(data.my_league);
        setMyPlayer(data.my_player);
      }
      setLoading(false);
    });
  }, [token]);

  // Load week matches when selected
  const loadWeek = useCallback(async (label: string) => {
    if (!token) return;
    setWeekLoading(true);
    setExpandedMatchId(null);
    const data: WeekMatchesResponse = await api.get(`/duel/archive/week/${label}`, token);
    if (data?.matchups) {
      setWeekData(data);
      // Load challenges for THIS specific archive week (not current week)
      const chalData = await api.get(`/challenges/week/${label}`, token);
      if (chalData?.challenges) setWeekChallenges(chalData.challenges);
    }
    setWeekLoading(false);
  }, [token]);

  useEffect(() => {
    if (selectedWeek) loadWeek(selectedWeek);
  }, [selectedWeek, loadWeek]);

  // Toggle match expansion (load calendar detail)
  function toggleMatch(matchupId: number) {
    if (expandedMatchId === matchupId) { setExpandedMatchId(null); setBarTip(null); return; }
    setExpandedMatchId(matchupId);
    setBarTip(null);
    if (!detailCache.current.has(matchupId)) {
      detailCache.current.set(matchupId, 'loading');
      api.get(`/duel/board/${matchupId}`, token!).then((d: MatchDetail) => {
        detailCache.current.set(matchupId, d);
        const loadStats = (pid: number) => {
          if (statsCache.current.has(pid)) return Promise.resolve(statsCache.current.get(pid)!);
          return api.get(`/player/${pid}/stats`, token!).then((res: any) => {
            if (res && res.wins !== undefined) {
              const s = res as PlayerStats;
              statsCache.current.set(pid, s);
              return s;
            }
            return null;
          }).catch(() => null);
        };
        Promise.all([loadStats(d.player_a.id), loadStats(d.player_b.id)]).then(([myS, oppS]) => {
          detailStatsCache.current.set(matchupId, { myStats: myS, oppStats: oppS });
          setRenderTick(t => t + 1);
        });
      });
    }
  }

  // Detect mobile
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: 700px)`);
    const check = () => setIsMobile(mq.matches);
    check();
    mq.addEventListener('change', check);
    return () => mq.removeEventListener('change', check);
  }, []);



  // Render calendar for expanded match
  function renderCalendar(m: WeekMatch, cycle: WeekMatchesResponse['cycle']) {
    const detail = detailCache.current.get(m.matchup_id);
    if (!detail || detail === 'loading') {
      return <div className="board-detail-loading">Loading calendar…</div>;
    }
    const opponent: Opponent = {
      id: m.player_b.id,
      display_name: m.player_b.display_name,
      avatar_url: m.player_b.avatar_url,
      level_value: m.player_b.level_value,
      is_bot: m.player_b.is_bot ? 1 : 0,
      matchup_id: m.matchup_id,
    };
    const dStats = detailStatsCache.current.get(m.matchup_id);
    // ── Points bar segments ──
    const aPts = Number(m.player_a.points) || 0;
    const bPts = Number(m.player_b.points) || 0;
    const maxPts = Math.max(aPts, bPts, 1);
    const aAllActs = detail.player_a.activities;
    const bAllActs = detail.player_b.activities;

    // Mobile: cumulative by day; Desktop: all-week
    const days = getDaysFromCycleStart(cycle.start_at_utc);
    const filterByDay = (acts: Activity[]): Activity[] => {
      if (!isMobile) return acts;
      const centerDay = days[mobileDayIdx];
      if (!centerDay) return acts;
      const cutoff = `${centerDay.getFullYear()}-${String(centerDay.getMonth()+1).padStart(2,'0')}-${String(centerDay.getDate()).padStart(2,'0')}`;
      return acts.filter(a => a.started_at_utc?.slice(0,10) <= cutoff);
    };
    const aActs = filterByDay(aAllActs);
    const bActs = filterByDay(bAllActs);
    const aSegs = buildSportSegments(aActs, maxPts);
    const bSegs = buildSportSegments(bActs, maxPts);

    const barKey = isMobile ? `bars-${mobileDayIdx}` : 'bars-all';

    const pointsBars = (
      <div key={barKey} className="archive-points-bars">
        <div className="archive-points-side archive-points-side--a">
          <div className="duel-points-bar-wrap">
            {aSegs.map((seg) => (
              <div key={seg.key}
                className="duel-points-bar-fill duel-points-bar-fill--sport"
                style={{ '--seg-pct': `${seg.widthPct}%`, backgroundColor: seg.color } as React.CSSProperties}
                onClick={(e) => {
                  if (!isTouch.current) return;
                  e.stopPropagation();
                  const r = e.currentTarget.getBoundingClientRect();
                  setBarTip({ x: r.left + r.width / 2, y: r.top - 4, seg });
                }}
                title={!isTouch.current ? seg.title : undefined}
              />
            ))}
          </div>
          {!isMobile && (
            <div className="sb-legend sb-legend--inline">
              {aSegs.map(seg => (
                <div key={seg.key} className="sb-legend-row">
                  <span className="sb-legend-dot" style={{ background: seg.color }} />
                  <span className="sb-legend-label">{seg.label}</span>
                  <span className="sb-legend-pts">{seg.title.split(': ')[1]}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <span className="archive-points-vs">vs</span>
        <div className="archive-points-side archive-points-side--b">
          <div className="duel-points-bar-wrap">
            {bSegs.map((seg) => (
              <div key={seg.key}
                className="duel-points-bar-fill duel-points-bar-fill--sport"
                style={{ '--seg-pct': `${seg.widthPct}%`, backgroundColor: seg.color } as React.CSSProperties}
                onClick={(e) => {
                  if (!isTouch.current) return;
                  e.stopPropagation();
                  const r = e.currentTarget.getBoundingClientRect();
                  setBarTip({ x: r.left + r.width / 2, y: r.top - 4, seg });
                }}
                title={!isTouch.current ? seg.title : undefined}
              />
            ))}
          </div>
          {!isMobile && (
            <div className="sb-legend sb-legend--inline">
              {bSegs.map(seg => (
                <div key={seg.key} className="sb-legend-row">
                  <span className="sb-legend-dot" style={{ background: seg.color }} />
                  <span className="sb-legend-label">{seg.label}</span>
                  <span className="sb-legend-pts">{seg.title.split(': ')[1]}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );

    return (
      <div className="board-detail-panel" onClick={(e) => { e.stopPropagation(); if (barTip) setBarTip(null); }}>
        {!isMobile && pointsBars}
        <WeeklyCalendar
          cycleStart={cycle.start_at_utc}
          myActivities={detail.player_a.activities}
          opponentActivities={detail.player_b.activities}
          myName={detail.player_a.display_name}
          myAvatar={detail.player_a.avatar_url || null}
          myIsBot={detail.player_a.is_bot}
          opponent={opponent}
          weekChallenges={weekChallenges}
          myConsolationPoints={detail.player_a.consolation_points ?? 0}
          opponentConsolationPoints={detail.player_b.consolation_points ?? 0}
          hidePoints={false}
          hideSpin
          myStats={dStats?.myStats ?? undefined}
          opponentStats={dStats?.oppStats ?? undefined}
          onDayChange={isMobile ? handleMobileDayChange : undefined}
          bottomSlot={isMobile ? pointsBars : undefined}
        />
        {barTip && createPortal(
          <div
            className="duel-bar-tooltip"
            style={{ position: 'fixed', top: barTip.y, left: barTip.x, transform: 'translateX(-50%) translateY(-100%)', marginBottom: 4, zIndex: 99999, pointerEvents: 'none' }}
          >
            <span className="sb-legend-dot" style={{ background: barTip.seg.color }} />
            {barTip.seg.key !== '_bonus' && barTip.seg.key !== '_pr'
              ? <FontAwesomeIcon icon={getSportIcon(barTip.seg.key)} className="sb-legend-icon" />
              : null}
            <span className="sb-legend-label">{barTip.seg.title.split(':')[0]}</span>
            <span className="sb-legend-pts">{barTip.seg.title.split(': ')[1]}</span>
          </div>,
          document.body
        )}
      </div>
    );
  }

  function formatWeek(label: string): string {
    const m = label.match(/^(\d{4})-W(\d+)$/);
    return m ? `KW\u00a0${m[2]}\u00a0/\u00a0${m[1]}` : label;
  }

  if (loading) return <div className="loading">Loading archive…</div>;

  return (
    <div className="archive-page">
      <header className="archive-header">
        <div className="archive-header-nav">
          <button className="btn-back" onClick={onBack}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>
        </div>
        <img src={logo} alt="NoWeakWeek" className="archive-logo" />
        <h1>Archive</h1>
      </header>

      {weeks.length === 0 ? (
        <p className="archive-empty">No completed weeks yet.</p>
      ) : (
        <div className="archive-stats-bar">
          <span>{weeks.length} Week{weeks.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Your Results Dropdown */}
      <div className="your-results-section">
        <div
          className={`your-results-header${yourResultsOpen ? ' your-results-header--open' : ''}`}
          onClick={() => {
            setYourResultsOpen(!yourResultsOpen);
            if (!yourResultsOpen && yourResults.length === 0) {
              loadYourResults(1);
              setYourResultsPage(1);
            }
          }}
        >
          <span className={`arch-week-chevron${yourResultsOpen ? ' arch-week-chevron--open' : ''}`}>
            <FontAwesomeIcon icon={faChevronDown} />
          </span>
          <span className="your-results-label">Your Results</span>
          <span className="arch-week-count">{yourResults.length > 0 ? `${yourResults.length} Duel${yourResults.length !== 1 ? 's' : ''}` : ''}</span>
        </div>

        {yourResultsOpen && (
          <div className="your-results-dropdown">
            {yourResultsLoading && yourResults.length === 0 ? (
              <div className="board-detail-loading">Loading your matches…</div>
            ) : yourResults.length === 0 ? (
              <div className="board-detail-loading">No completed matches yet.</div>
            ) : (
              <>
                <div className="archive-duel-table-header">
                  <span className="duel-col duel-col--onfire">🔥</span>
                  <span className="duel-col duel-col--player"></span>
                  <span className="duel-col duel-col--score">🏆</span>
                  <span className="duel-col duel-col--colon"></span>
                  <span className="duel-col duel-col--score">🏆</span>
                  <span className="duel-col duel-col--player"></span>
                  <span className="duel-col duel-col--onfire">🔥</span>
                  <span className="duel-col duel-col--replay"></span>
                </div>
                <div className="board-league-list">
                  {yourResults.map((m: any) => {
                    const isWin = m.result === 'win';
                    const isDraw = m.result === 'draw';
                    const myPts = Math.round(Number(m.my_points) || 0);
                    const oppPts = Math.round(Number(m.opp_points) || 0);
                    const opp = m.opponent;
                    const oppElo = opp?.elo_rating ?? 0;
                    const myElo = m.my_elo ?? 0;
                    const mkAvatar = (p: any) =>
                      p?.is_bot
                        ? <span className="duel-avatar duel-avatar--bot"><FontAwesomeIcon icon={faRobot} /></span>
                        : p?.avatar_url
                          ? <img src={p.avatar_url} alt="" className="duel-avatar" style={{ cursor: 'pointer' }}
                              onClick={(e) => { e.stopPropagation(); onNavigateToPlayerProfile?.(p.id); }} />
                          : <span className="duel-avatar duel-avatar--ph" style={{ cursor: 'pointer' }}
                              onClick={(e) => { e.stopPropagation(); onNavigateToPlayerProfile?.(p.id); }}>{p?.display_name?.charAt(0) ?? '?'}</span>;

                    return (
                      <React.Fragment key={m.matchup_id}>
                        <div
                          className={`archive-duel-table-row archive-duel-table-row--mine${expandedMatchId === m.matchup_id ? ' archive-duel-table-row--expanded' : ''}`}
                          onClick={() => toggleMatch(m.matchup_id)}>

                          <span className={`duel-col duel-col--onfire${isWin ? ' duel-col--winner' : ''}`}
                            style={{ color: getEloColor(myElo) }}>
                            {myElo || '—'}
                            {!isDraw && (
                              <span style={{ fontSize: '0.65rem', fontWeight: 700, marginLeft: 3, color: isWin ? '#4caf50' : '#f44336' }}>
                                {isWin ? '+20' : '-20'}
                              </span>
                            )}
                          </span>
                          <span className={`duel-col duel-col--player${isWin ? ' duel-col--winner' : ''}`}>
                            {myPlayer?.avatar_url
                              ? <img src={myPlayer.avatar_url} alt="" className="duel-avatar" style={{ cursor: 'pointer' }}
                                  onClick={(e) => { e.stopPropagation(); onNavigateToPlayerProfile?.(myPlayer!.id); }} />
                              : <span className="duel-avatar duel-avatar--ph" style={{ cursor: 'pointer' }}
                                  onClick={(e) => { e.stopPropagation(); if (myPlayer) onNavigateToPlayerProfile?.(myPlayer.id); }}>{(myPlayer?.display_name ?? '?').charAt(0)}</span>
                            }
                            <span className="duel-player-name">
                              {myPlayer?.display_name ?? ''}{isWin && <FontAwesomeIcon icon={faTrophy} className="duel-trophy-icon duel-trophy-icon--inline" />}
                            </span>
                          </span>
                          <span className={`duel-col duel-col--score${isWin ? ' duel-col--winner-score' : ''}`}>
                            {myPts}
                          </span>

                          <span className="duel-col duel-col--colon">
                            <span className="duel-vs-text">vs</span>
                          </span>

                          <span className={`duel-col duel-col--score${!isWin && !isDraw ? ' duel-col--winner-score' : ''}`}>
                            {oppPts}
                          </span>
                          <span className={`duel-col duel-col--player duel-col--player--right${!isWin && !isDraw ? ' duel-col--winner' : ''}`}>
                            {!isWin && !isDraw && <FontAwesomeIcon icon={faTrophy} className="duel-trophy-icon duel-trophy-icon--inline" />}
                            <span className="duel-player-name">
                              {opp?.display_name ?? '?'}
                            </span>
                            {opp?.is_paused && <span className="player-paused-badge" title="pausiert – keine Strava-Verbindung">⏸</span>}
                            {mkAvatar(opp)}
                          </span>
                          <span className={`duel-col duel-col--onfire${!isWin && !isDraw ? ' duel-col--winner' : ''}`}
                            style={{ color: opp?.is_bot ? '#666' : getEloColor(oppElo) }}>
                            {opp?.is_bot ? '0' : (oppElo || '—')}
                          </span>

                          <span className="duel-col duel-col--replay" onClick={e => { e.stopPropagation(); onReplay(m.matchup_id); }}>
                            <button className="arch-row-replay-btn" title="Replay">▶</button>
                          </span>
                        </div>
                        {expandedMatchId === m.matchup_id && (
                          <div key={`detail-${m.matchup_id}`} className="duel-table-detail" onClick={e => e.stopPropagation()}>
                            {(() => {
                              const detail = detailCache.current.get(m.matchup_id);
                              if (!detail || detail === 'loading') {
                                return <div className="board-detail-loading">Loading calendar…</div>;
                              }
                              const opponent: Opponent = {
                                id: detail.player_b.id,
                                display_name: detail.player_b.display_name,
                                avatar_url: detail.player_b.avatar_url,
                                level_value: detail.player_b.level_value,
                                is_bot: detail.player_b.is_bot ? 1 : 0,
                                matchup_id: m.matchup_id,
                              };
                              const dStats = detailStatsCache.current.get(m.matchup_id);
                              return (
                                <WeeklyCalendar
                                  cycleStart={detail.cycle.start_at_utc}
                                  myActivities={detail.player_a.activities}
                                  opponentActivities={detail.player_b.activities}
                                  myName={detail.player_a.display_name}
                                  myAvatar={detail.player_a.avatar_url || null}
                                  myIsBot={detail.player_a.is_bot}
                                  myId={detail.player_a.id}
                                  onNavigateToPlayerProfile={onNavigateToPlayerProfile}
                                  opponent={opponent}
                                  weekChallenges={weekChallenges}
                                  myConsolationPoints={detail.player_a.consolation_points ?? 0}
                                  opponentConsolationPoints={detail.player_b.consolation_points ?? 0}
                                  hidePoints={false}
                                  hideSpin
                                  myStats={dStats?.myStats ?? undefined}
                                  opponentStats={dStats?.oppStats ?? undefined}
                                />
                              );
                            })()}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
                {yourResultsHasMore && (
                  <div className="your-results-load-more">
                    <button
                      className="your-results-load-more-btn"
                      onClick={() => {
                        const nextPage = yourResultsPage + 1;
                        setYourResultsPage(nextPage);
                        loadYourResults(nextPage, true);
                      }}
                      disabled={yourResultsLoading}
                    >
                      {yourResultsLoading ? 'Loading…' : 'Load more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Week list */}
      <div className="arch-weeks-list">
        {weeks.map(w => (
          <div key={w.week_label}>
            {/* Week header (clickable) */}
            <div
              className={`arch-week-row${selectedWeek === w.week_label ? ' arch-week-row--selected' : ''}`}
              onClick={() => setSelectedWeek(selectedWeek === w.week_label ? null : w.week_label)}
            >
              <span className={`arch-week-chevron${selectedWeek === w.week_label ? ' arch-week-chevron--open' : ''}`}>
                <FontAwesomeIcon icon={faChevronDown} />
              </span>
              <span className="arch-week-label">{formatWeek(w.week_label)}</span>
              <span className="arch-week-count">{w.match_count} Duel{w.match_count !== 1 ? 's' : ''}</span>
            </div>

            {/* Expanded: matches for this week */}
            {selectedWeek === w.week_label && (
              <div className="arch-week-expanded">
                {weekLoading ? (
                  <div className="board-detail-loading">Loading matches…</div>
                ) : weekData && (
                  <div className="arch-league-matches">
                    {/* Spalten-Header */}
                    <div className="archive-duel-table-header">
                      <span className="duel-col duel-col--onfire">🔥</span>
                      <span className="duel-col duel-col--player"></span>
                      <span className="duel-col duel-col--score">🏆</span>
                      <span className="duel-col duel-col--colon"></span>
                      <span className="duel-col duel-col--score">🏆</span>
                      <span className="duel-col duel-col--player"></span>
                      <span className="duel-col duel-col--onfire">🔥</span>
                      <span className="duel-col duel-col--replay"></span>
                    </div>

                    <div className="board-league-list">
                      {weekData.matchups.map(m => {
                        const aWins = m.winner_id === m.player_a.id;
                        const bWins = m.winner_id === m.player_b.id;
                        const isMyMatch = m.is_my_match;
                        const aElo = (m.player_a as any).elo_rating;
                        const bElo = (m.player_b as any).elo_rating;

                        const mkAvatar = (p: WeekMatchPlayer) =>
                          p.is_bot
                            ? <span className="duel-avatar duel-avatar--bot"><FontAwesomeIcon icon={faRobot} /></span>
                            : p.avatar_url
                              ? <img src={p.avatar_url} alt="" className="duel-avatar" style={{ cursor: 'pointer' }}
                                  onClick={(e) => { e.stopPropagation(); onNavigateToPlayerProfile?.(p.id); }} />
                              : <span className="duel-avatar duel-avatar--ph" style={{ cursor: 'pointer' }}
                                  onClick={(e) => { e.stopPropagation(); onNavigateToPlayerProfile?.(p.id); }}>{p.display_name.charAt(0)}</span>;

                        return (
                          <React.Fragment key={m.matchup_id}>
                          <div
                            className={`archive-duel-table-row${isMyMatch ? ' archive-duel-table-row--mine' : ''}${expandedMatchId === m.matchup_id ? ' archive-duel-table-row--expanded' : ''}`}
                            onClick={() => toggleMatch(m.matchup_id)}>

                            {/* Player A (links) */}
                            <span className={`duel-col duel-col--onfire${aWins ? ' duel-col--winner' : ''}`}
                              style={{ color: m.player_a.is_bot ? '#666' : getEloColor(aElo ?? 1000) }}>
                              {m.player_a.is_bot ? '0' : (aElo ?? '—')}
                              {!m.player_a.is_bot && (m.player_a as any).elo_change !== 0 && m.winner_id !== null && (
                                <span style={{ fontSize: '0.65rem', fontWeight: 700, marginLeft: 3, color: (m.player_a as any).elo_change > 0 ? '#4caf50' : '#f44336' }}>
                                  {(m.player_a as any).elo_change > 0 ? '+' : ''}{(m.player_a as any).elo_change}
                                </span>
                              )}
                            </span>
                            <span className={`duel-col duel-col--player${aWins ? ' duel-col--winner' : ''}`}>
                              {mkAvatar(m.player_a)}
                              <span className="duel-player-name">
                                {m.player_a.display_name}
                                {aWins && <FontAwesomeIcon icon={faTrophy} className="duel-trophy-icon duel-trophy-icon--inline" />}
                              </span>
                            </span>
                            <span className={`duel-col duel-col--score${aWins ? ' duel-col--winner-score' : ''}`}>
                              {Math.round(Number(m.player_a.points) || 0)}
                            </span>

                            {/* Status-Trennzeichen */}
                            <span className="duel-col duel-col--colon">
                              <span className="duel-vs-text">vs</span>
                            </span>

                            {/* Player B (rechts) */}
                            <span className={`duel-col duel-col--score${bWins ? ' duel-col--winner-score' : ''}`}>
                              {Math.round(Number(m.player_b.points) || 0)}
                            </span>
                            <span className={`duel-col duel-col--player duel-col--player--right${bWins ? ' duel-col--winner' : ''}`}>
                              {bWins && <FontAwesomeIcon icon={faTrophy} className="duel-trophy-icon duel-trophy-icon--inline" />}
                              <span className="duel-player-name">
                                {m.player_b.display_name}
                              </span>
                              {mkAvatar(m.player_b)}
                            </span>
                            <span className={`duel-col duel-col--onfire${bWins ? ' duel-col--winner' : ''}`}
                              style={{ color: m.player_b.is_bot ? '#666' : getEloColor(bElo ?? 1000) }}>
                              {m.player_b.is_bot ? '0' : (bElo ?? '—')}
                              {!m.player_b.is_bot && (m.player_b as any).elo_change !== 0 && m.winner_id !== null && (
                                <span style={{ fontSize: '0.65rem', fontWeight: 700, marginLeft: 3, color: (m.player_b as any).elo_change > 0 ? '#4caf50' : '#f44336' }}>
                                  {(m.player_b as any).elo_change > 0 ? '+' : ''}{(m.player_b as any).elo_change}
                                </span>
                              )}
                            </span>

                            {/* Replay Button (eigene Spalte) */}
                            <span className="duel-col duel-col--replay" onClick={e => { e.stopPropagation(); onReplay(m.matchup_id); }}>
                              <button className="arch-row-replay-btn" title="Replay">▶</button>
                            </span>
                          </div>
                            {/* Expandierter Kalender – als Geschwister-Element */}
                            {expandedMatchId === m.matchup_id && (
                              <div key={`detail-${m.matchup_id}`} className="duel-table-detail" onClick={e => e.stopPropagation()}>
                                {renderCalendar(m, weekData.cycle)}
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Helper: Player avatar + name cell ── */

function PlayerCell({ player, right }: { player: WeekMatchPlayer; right?: boolean }) {
  const avatar = player.is_bot
    ? <span className="arch-row-avatar arch-row-avatar--bot"><FontAwesomeIcon icon={faRobot} /></span>
    : player.avatar_url
      ? <img src={player.avatar_url} alt="" className="arch-row-avatar" />
      : <span className="arch-row-avatar arch-row-avatar--ph">{player.display_name.charAt(0)}</span>;
  return (
    <>
      {avatar}
      <span className={`arch-row-playername${right ? ' arch-row-playername--right' : ''}`}>{player.display_name}</span>
    </>
  );
}
